import React from "react"
import { SubmitHandler } from "react-hook-form"

import type { UseFormReturn } from "react-hook-form"

import { PageHeader } from "@/components/PageHeader"
import { Content } from "@/components/QuorumFormProvider/Content"
import { ErrorSummary } from "@/components/QuorumFormProvider/ErrorSummary"
import { Footer } from "@/components/QuorumFormProvider/Footer"
import { Root } from "@/components/QuorumFormProvider/Root"

export interface QuorumFormProviderProps<T> {
    /** The object returned by React Hook Form's [useForm](https://react-hook-form.com/docs/useform) */
    reactHookForm: UseFormReturn<T>
    /** Function to pass to React Hook Form's [handleSubmit](https://react-hook-form.com/docs/useform/handlesubmit) */
    submitForm: SubmitHandler<T>
    /** Title to display in the top bar */
    title: string
    /** Label for Cancel button */
    cancelLabel?: string
    /** Disable Cancel button */
    cancelDisabled?: boolean
    /** Label for Submit button */
    submitLabel?: string
    /** Disable Submit Button */
    submitDisabled?: boolean
    /** Right section of the header */
    rightSection?: React.ReactNode
    /** Hide Footer */
    hideFooter?: boolean
    /** Alternative Footer  */
    altFooter?: React.ReactNode
    /** Hide Back Button */
    hideBackButton?: boolean
    /** Hide Error Summary */
    hideErrorSummary?: boolean
    /** Remove left empty space in Error Summary */
    /** Typically used when the Form is centered and has a form stepper on the top */
    noLeftSpaceOnErrorSummary?: boolean
    /** Disable HTML5 validation */
    noValidate?: boolean
}

export const QuorumFormProvider = <T,>({
    children,
    hideFooter,
    reactHookForm,
    rightSection,
    submitForm,
    cancelLabel,
    cancelDisabled,
    submitLabel,
    submitDisabled,
    title,
    altFooter,
    hideBackButton,
    hideErrorSummary,
    noLeftSpaceOnErrorSummary,
    noValidate,
}: React.PropsWithChildren<QuorumFormProviderProps<T>>) => {
    return (
        <Root reactHookForm={reactHookForm} submitForm={submitForm} noValidate={noValidate}>
            <PageHeader title={title} rightSection={rightSection} hideBackButton={hideBackButton} />

            {!hideErrorSummary && <ErrorSummary noLeftEmptySpace={noLeftSpaceOnErrorSummary} />}

            <Content>{children}</Content>
            {!hideFooter && altFooter && altFooter}
            {!hideFooter && !altFooter && (
                <Footer
                    cancelLabel={cancelLabel}
                    cancelDisabled={cancelDisabled}
                    submitLabel={submitLabel}
                    submitDisabled={submitDisabled}
                />
            )}
        </Root>
    )
}

QuorumFormProvider.Content = Content
QuorumFormProvider.ErrorSummary = ErrorSummary
QuorumFormProvider.Footer = Footer
QuorumFormProvider.Root = Root
